export type AppRouteMode =
  | 'workbook'
  | 'cfo_core'
  | 'client_workspace'
  | 'job_inputs'
  | 'admin_shell'
  | 'cfo_landing'
  | 'cfo_pricing'
  | 'cfo_signup'
  | 'cfo_demo_entry'
  | 'tour_share_entry'
  | 'cfo_onboarding'
  | 'cfo_connect_checklist'
  | 'billing_checkout_complete'
  | 'partner_billing'
  | 'cfo_legal_privacy'
  | 'cfo_legal_terms'
  | 'founder_activity'
  | 'firm_cockpit'
  | 'excel_mockups'
  | 'excel_live_shell'
  | 'surface_admin_fixture'
  | 'surface_datasource_fixture'
  | 'surface_viz_authoring_fixture'
  | 'surface_viz_edit_fixture'

export function readAppRouteMode(
  loc: Pick<Location, 'pathname' | 'search'> | null | undefined,
): AppRouteMode {
  const pathname = String(loc?.pathname || '')
  const search = String(loc?.search || '')
  const params = new URLSearchParams(search)
  if (pathname.includes('/dev/excel-ui-live-shell') || params.get('fg_excel_live_shell') === '1') {
    return 'excel_live_shell'
  }
  if (pathname.includes('/dev/surface-admin') || params.get('fg_surface_admin') === '1') {
    return 'surface_admin_fixture'
  }
  if (pathname.includes('/dev/surface-datasource') || params.get('fg_surface_datasource') === '1') {
    return 'surface_datasource_fixture'
  }
  if (pathname.includes('/dev/surface-viz-authoring') || params.get('fg_surface_viz_authoring') === '1') {
    return 'surface_viz_authoring_fixture'
  }
  if (pathname.includes('/dev/surface-viz-edit') || params.get('fg_surface_viz_edit') === '1') {
    return 'surface_viz_edit_fixture'
  }
  if (pathname.includes('/dev/excel-ui-mockups') || params.get('fg_excel_ui_mockups') === '1') {
    return 'excel_mockups'
  }
  const parts = pathname.split('/').filter(Boolean)
  // CFO Core marketing + self-serve onboarding surfaces. The bare root is the
  // marketing landing, except the legacy `?view=tableau-creator` escape hatch
  // which stays on the admin shell.
  if (parts.length === 0) {
    if (params.get('view') === 'tableau-creator') {
      return 'admin_shell'
    }
    return 'cfo_landing'
  }
  if (parts.length === 1 && parts[0] === 'pricing') {
    return 'cfo_pricing'
  }
  if (parts.length === 1 && parts[0] === 'signup') {
    return 'cfo_signup'
  }
  // Personalized tour share link: /t/<token>. Short on purpose — these go in
  // sales emails, where a long path invites truncation and mis-copying.
  if (parts.length === 2 && parts[0] === 't' && parts[1]) {
    return 'tour_share_entry'
  }
  // One-click live demo: mints a read-only session on the sample firm site.
  if (parts.length === 1 && parts[0] === 'demo') {
    return 'cfo_demo_entry'
  }
  if (parts.length === 2 && parts[0] === 'demo' && parts[1] === 'owner') {
    return 'cfo_demo_entry'
  }
  // Franchise-fleet connect checklist: many QBO companies, one consent each.
  if (parts.length >= 2 && parts[0] === 'onboarding' && parts[1] === 'connect-checklist') {
    return 'cfo_connect_checklist'
  }
  if (parts.length >= 1 && parts[0] === 'onboarding' && (parts[1] === 'sync-started' || parts.length === 1)) {
    return 'cfo_onboarding'
  }
  if (parts.length === 3 && parts[0] === 'billing' && parts[1] === 'checkout' && parts[2] === 'complete') {
    return 'billing_checkout_complete'
  }
  if (parts.length === 2 && parts[0] === 'billing' && parts[1] === 'partner') {
    return 'partner_billing'
  }
  if (parts.length === 1 && (parts[0] === 'privacy' || parts[0] === 'privacy.html')) {
    return 'cfo_legal_privacy'
  }
  if (parts.length === 1 && (parts[0] === 'terms' || parts[0] === 'eula' || parts[0] === 'eula.html')) {
    return 'cfo_legal_terms'
  }
  if (parts.length === 2 && parts[0] === 'founder' && parts[1] === 'activity') {
    return 'founder_activity'
  }
  // Firm Cockpit: /<siteSlug>/firm[/...] — the accounting-firm command center over the site's clients.
  if (parts.length >= 2 && parts[1] === 'firm') {
    return 'firm_cockpit'
  }
  if (parts.length === 2 && parts[1] === 'workbooks') {
    if (params.get('view') === 'tableau-creator') {
      return 'admin_shell'
    }
    return 'cfo_landing'
  }
  if (parts.length >= 4 && parts[1] === 'workbooks' && parts[3] === 'cfo') {
    return 'cfo_core'
  }
  if (parts.length === 4 && parts[1] === 'workbooks' && parts[3] === 'job-inputs') {
    return 'job_inputs'
  }
  if (parts.length >= 4 && parts[1] === 'workbooks' && parts[3] === 'workspace') {
    return 'client_workspace'
  }
  if (parts.length >= 4 && parts[1] === 'workbooks' && parts[3] === 'cfo-home') {
    const params = new URLSearchParams(loc?.search || '')
    if (
      String(params.get('fg_cfo_section') || '').trim() ||
      String(params.get('fg_cfo_view') || '').trim()
    ) {
      return 'cfo_core'
    }
  }
  if (parts.length >= 3 && parts[1] === 'workbooks') {
    return 'workbook'
  }
  return 'admin_shell'
}
